import { Component, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { ConfigurationService } from '../core/configuration.service';
import { DialogService } from '../core/dialog/dialog.service';
import { UserService } from '../core/user/user.service';
import { Observable, Subscription } from '../core/util/rxjs';
import { LocaleService } from '../core/locale/locale.service';
import { LoginConfig } from 'customer-portal/core/login-config.model';
import { LoginOption } from '../core/login-option.model';

@Component({
  selector: 'cp-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.less']
})
export class LoginComponent implements OnDestroy, OnInit {
  emailControl: UntypedFormControl;
  passwordControl: UntypedFormControl;
  form: UntypedFormGroup;

  validate = false;
  serverError: string | undefined;
  config$: Observable<LoginConfig>;
  hadUnsupportedLanguage$: Observable<boolean>;

  loginOption = LoginOption;

  @Output()
  prefixLabel: string;

  private loginInProgress = false;
  private formSubscription: Subscription;

  constructor(private configuration: ConfigurationService, private userService: UserService,
              private router: Router, private activatedRoute: ActivatedRoute,
              private localeService: LocaleService,
              private dialogService: DialogService, fb: UntypedFormBuilder) {
    this.emailControl = new UntypedFormControl('', [Validators.email]);
    this.passwordControl = new UntypedFormControl('', [Validators.required]);

    this.form = fb.group({
      email: this.emailControl,
      password: this.passwordControl
    });
  }

  ngOnInit() {
    this.config$ = this.configuration.getLoginConfig(true);
    this.hadUnsupportedLanguage$ = this.localeService.hadUnsupportedLanguage$;
    this.serverError = undefined;

    this.prefixLabel = "Login.logInWith";

    this.activatedRoute.queryParams.subscribe(params => {
      if (params['oidc'] !== null && params['oidc'])
      {
        let loginId = localStorage.getItem("oidcLoginId");
        if (loginId !== null && loginId !== "") {
          this.userService.oidcLogin(loginId).subscribe(
            () => this.confirmLogin(),
            error => this.handleLoginError(error));
        }
      }
    })
  }

  ngOnDestroy() {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  isServerCompatible(config: LoginConfig) {
    const mqParts = config.version.memoQ.split('.');
    const cpParts = config.version.customerPortal.split('.');
    return mqParts[0] === cpParts[0] && mqParts[1] === cpParts[1];
  }

  login() {
    this.validate = true;
    if (!this.form.valid || this.loginInProgress) {
      return;
    }

    this.loginInProgress = true;
    this.userService.login(this.form.value).subscribe(
      () => this.confirmLogin(),
      error => this.handleLoginError(error));
  }

  oidcAuth(oidcName: string) {
    this.userService.getOidcAuthParameters().subscribe(oidcAuthParameters => {
      // store oidcLoginParameters.LoginId and OidcLoginParameters.LoginIdHash in locale storage for later use
      localStorage.setItem("oidcLoginId", oidcAuthParameters.LoginId);
      localStorage.setItem("oidcLoginIdHash", oidcAuthParameters.LoginIdHash);
      let fullUrl = window.location.href;
      let currentUrlWithoutLastSegment = fullUrl.substring(0, fullUrl.lastIndexOf('/')); // remove /login
      window.location.href = currentUrlWithoutLastSegment + "/api/account/oidc-auth?id_provider=" + oidcName
        + "&login_id_hash=" + oidcAuthParameters.LoginIdHash
        + "&redirect_uri=" + window.location.href + "?oidc=true";
    }, (error) => {
      console.log(error);
    });
  }

  private confirmLogin() {
    this.userService.confirmLogin().subscribe(profile => {
      this.handleSuccessfulLogin(profile);
    }, (error: shared.ErrorResponse) => {
      this.loginInProgress = false;
      this.serverError = error.LocalizationKey;
    });
  }

  private handleLoginError(error: shared.ErrorResponse) {
    this.loginInProgress = false;
    if (!error) {
      return;
    }

    if (error.ErrorCode === 'AlreadyLoggedIn') {
      this.dialogService
        .confirmation({ onConfirmed: () => this.confirmLogin(), confirmName: 'Login.login' })
        .title('Login.existingSession')
        .messages('Login.otherSessionActive')
        .show();
    } else {
      this.serverError = error.LocalizationKey;
    }
  }

  private handleSuccessfulLogin(userProfile: accounts.UserProfile) {
      this.serverError = undefined;
      this.loginInProgress = false;

      this.userService.setFromProfile(userProfile);
      const redirectUrl = this.activatedRoute.snapshot.queryParams['url'];
      if (redirectUrl) {
          this.router.navigateByUrl(decodeURIComponent(redirectUrl));
      } else {
          this.router.navigate(['translation-jobs', 'dashboard']);
      }
  }}
